import { runtimeConfig } from './runtime.ts';

/**
 * The testing accounts a demo deployment offers as one-click sign-ins.
 *
 * Read from the deployment's own configuration at run time, and empty
 * everywhere else: a deployment that does not name any accounts shows no
 * panel at all. The value is `Label=address`, separated by `|`:
 *
 *   The conductor=suzie@example.dev.ben.ai|An applicant=applicant01@example.dev.ben.ai
 *
 * The password is deliberately not part of this. The buttons only prefill the
 * address on Logto's own sign-in page — whoever is testing still has to know
 * the password, which is the one thing standing between a shared URL and
 * somebody signing in as the team.
 */
export interface DemoAccount { label: string; email: string }

/** Parses the list, dropping anything malformed rather than throwing: a typo
 *  in an environment variable must not take the front page down with it. */
export function parseDemoAccounts(raw: string | undefined): DemoAccount[] {
  if (!raw) return [];
  return raw
    .split('|')
    .map((entry) => {
      const at = entry.indexOf('=');
      if (at < 1) return null;
      const label = entry.slice(0, at).trim();
      const email = entry.slice(at + 1).trim();
      if (!label || !email.includes('@')) return null;
      return { label, email };
    })
    .filter((a): a is DemoAccount => a !== null);
}

/**
 * Asked for when it is needed, never at import.
 *
 * A `const` here read the configuration while the modules were still being
 * evaluated — which is before `main.ts` has run a line of its own, and so
 * before the deployment's own configuration has been fetched. It would have
 * captured whatever was baked into the bundle: empty, on every image built for
 * somebody else's host, and the panel would have quietly never appeared.
 */
export function demoAccounts(): DemoAccount[] {
  return parseDemoAccounts(runtimeConfig().demoAccounts);
}

/**
 * Where a pending "come back as this person" is kept across a sign-out.
 *
 * Not the URL: Logto matches a post-logout address against the registered
 * list exactly, and `/?as=someone` is not `/`, so it is refused and the person
 * is left on the identity provider's page. Session storage survives the round
 * trip, belongs to this tab, and is gone when the tab is.
 */
const SWITCH_KEY = 'jcao.signInAs';

export function rememberSwitch(email: string): void {
  try { window.sessionStorage.setItem(SWITCH_KEY, email); } catch { /* private window */ }
}

/** Reads the pending switch and forgets it, so a failure is not a loop. */
export function takeSwitch(): string | null {
  try {
    const email = window.sessionStorage.getItem(SWITCH_KEY);
    window.sessionStorage.removeItem(SWITCH_KEY);
    return email;
  } catch {
    return null;
  }
}
